"use client"

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react"
import styles from "./styles.module.css"
import Button from "../../modules/Button/Button"
import { FaRegEdit, FaCheck } from "react-icons/fa"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import AddDevice from "../add-device"
import SerialInput from "../../modules/SerialInput"
import Select from "../../modules/Select/Select"
import toast from "react-hot-toast"
import { getWarehouseBoxDetail } from "../../../../services/inventory/detail"

const AddDevices = ({
  boxDetails,
  previewDevices = [],
  updateBox,
  onDevicesChange,
  deviceErrors = [],
  onRefresh,
}) => {
  const [box, setBox] = useState(null)
  const [loadingBox, setLoadingBox] = useState(false)
  const [devices, setDevices] = useState([])
  const [showAddDevice, setShowAddDevice] = useState(false)
  const [editingDevice, setEditingDevice] = useState(null)
  const [inlineEditingIndex, setInlineEditingIndex] = useState(null)
  const [editFormData, setEditFormData] = useState({ serial: "", device_type: "" })
  const [saveLoadingIndex, setSaveLoadingIndex] = useState(null) // loading for inline save index
  const [removeLoading, setRemoveLoading] = useState({}) // per-index remove loading

  // local client-side errors keyed by index (so we can show inline errors without waiting server)
  const [localDeviceErrors, setLocalDeviceErrors] = useState({})

  // refs to avoid refetching same box & to track mounted state
  const mountedRef = useRef(false)
  const lastFetchedBoxRef = useRef(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // normalize helpers
  const normalizeDevices = useCallback(
    (arr) =>
      Array.isArray(arr)
        ? arr.map((d) => ({
            ...d,
            serial: d.serial ?? d.serial_number ?? "",
            device_type: d.device_type ?? d.device_type_id ?? null,
            // ensure purchase_date exists on normalized object if present
            purchase_date: d.purchase_date ?? d.purchaseDate ?? null,
          }))
        : [],
    [],
  )

  // fetch box details — guard by lastFetchedBoxRef to prevent duplicate calls
  const fetchBox = useCallback(
    async (boxDetailsVal) => {
      const boxId = typeof boxDetailsVal === "number" || typeof boxDetailsVal === "string" ? boxDetailsVal : boxDetailsVal && boxDetailsVal.id
      if (!boxId) {
        setBox(null)
        lastFetchedBoxRef.current = null
        return
      }

      // if already fetched this id and box exists, don't re-fetch
      if (String(lastFetchedBoxRef.current) === String(boxId) && box) {
        return
      }

      lastFetchedBoxRef.current = boxId
      setLoadingBox(true)
      try {
        const res = await getWarehouseBoxDetail(boxId)
        if (!mountedRef.current) return
        if (res && res.success && res.data) {
          const apiBox = res.data
          const normalized = {
            ...apiBox,
            compatible_device_types: apiBox.box_type_compatible ?? apiBox.compatible_device_types ?? [],
            box_type_compatible: apiBox.box_type_compatible ?? apiBox.compatible_device_types ?? [],
          }
          setBox(normalized)
        } else {
          setBox(null)
          toast.error(res?.message || "Failed to fetch box details")
        }
      } catch (err) {
        console.error("getWarehouseBoxDetail error:", err)
        toast.error("Failed to load box details (see console).")
        setBox(null)
      } finally {
        if (mountedRef.current) setLoadingBox(false)
      }
    },
    [box],
  )

  // effect: fetch box when boxDetails changes — use fetchBox guard
  useEffect(() => {
    fetchBox(boxDetails)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxDetails]) // only when boxDetails identity changes

  // effect: set devices from previewDevices or box.devices — only when those change
  useEffect(() => {
    if (Array.isArray(previewDevices) && previewDevices.length > 0) {
      setDevices(normalizeDevices(previewDevices))
    } else if (box && Array.isArray(box.devices)) {
      setDevices(normalizeDevices(box.devices))
    } else {
      setDevices([])
    }
    // reset client-side errors when preview changes
    setLocalDeviceErrors({})
  }, [previewDevices, box, normalizeDevices])

  // serial normalization & duplicate check
  const normalizeSerial = useCallback((s) => (s || "").toString().trim().toLowerCase(), [])
  const hasDuplicateSerial = useCallback(
    (serial, excludeIndex = null) => {
      const norm = normalizeSerial(serial)
      if (!norm) return false
      return devices.some((d, i) => {
        if (excludeIndex !== null && i === excludeIndex) return false
        return normalizeSerial(d.serial) === norm
      })
    },
    [devices, normalizeSerial],
  )

  // compatible types & options
  const compatibleTypes = box?.compatible_device_types ?? box?.box_type_compatible ?? []
  const typeOptions = useMemo(() => compatibleTypes.map((t) => ({ label: t.name, value: t.id })), [compatibleTypes])

  const getDeviceTypeName = useCallback(
    (deviceTypeId) => {
      const found = compatibleTypes.find((t) => String(t.id) === String(deviceTypeId))
      return found ? found.name : "Unknown"
    },
    [compatibleTypes],
  )

  const getBoxTypeLabel = useCallback((status) => (status === "new" ? "New" : status === "refurbished" ? "Refurbished" : status || "Unknown"), [])

  // persist devices: only call parent once with normalized array — no extra loading toggles here
  const persistDevices = useCallback(
    async (newDevices) => {
      // update local UI immediately for snappy UX
      setDevices(newDevices)

      const boxId = box?.id ?? (typeof boxDetails === "number" || typeof boxDetails === "string" ? boxDetails : null)
      if (!boxId) {
        // don't call parent if no valid box id
        return
      }
      try {
        // pass to parent — keep it minimal, parent handles server calls
        await onDevicesChange(boxId, newDevices)
      } catch (err) {
        console.error("onDevicesChange error:", err)
        toast.error("Failed to update devices. See console.")
        throw err
      }
    },
    [box, boxDetails, onDevicesChange],
  )

  // Utility: set a local inline error for a given index
  const setLocalErrorForIndex = useCallback((index, errorObj) => {
    setLocalDeviceErrors((prev) => ({ ...prev, [index]: errorObj }))
  }, [])

  // Utility: clear local error for index
  const clearLocalErrorForIndex = useCallback((index) => {
    setLocalDeviceErrors((prev) => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }, [])

  // add or update single device
  const setDevice = useCallback(
    async (device) => {
      const serial = device.serial ?? device.serial_number ?? ""
      // if editing existing device
      if (editingDevice !== null) {
        const idx = editingDevice.index
        if (hasDuplicateSerial(serial, idx)) {
          // set inline error for that index so UI shows it
          setLocalErrorForIndex(idx, { field: "serial", message: "Duplicate IMEI/Gateway ID detected. Please use a unique serial." })
          toast.error("Duplicate IMEI/Gateway ID detected. Please use a unique serial.")
          return
        } else {
          // clear any local error for this index
          clearLocalErrorForIndex(idx)
        }

        const newDevices = devices.map((d, i) => (i === idx ? { ...d, ...device, serial } : d))
        const prev = devices
        setDevices(newDevices)
        try {
          await persistDevices(newDevices)
          setEditingDevice(null)
        } catch {
          setDevices(prev)
        }
      } else {
        // new device (will be appended at index = devices.length)
        const nextIndex = devices.length
        if (hasDuplicateSerial(serial, null)) {
          // mark inline error on the would-be index so the user sees which row is duplicated
          setLocalErrorForIndex(nextIndex, { field: "serial", message: "Duplicate IMEI/Gateway ID detected. Please use a unique serial." })
          toast.error("Duplicate IMEI/Gateway ID detected. Please use a unique serial.")
          return
        } else {
          // clear any local error (just in case)
          clearLocalErrorForIndex(nextIndex)
        }

        const newDevices = [...devices, { ...device, serial }]
        const prev = devices
        setDevices(newDevices)
        try {
          await persistDevices(newDevices)
        } catch {
          setDevices(prev)
        }
      }

      setShowAddDevice(false)
      if (onRefresh) await onRefresh()
    },
    [devices, editingDevice, hasDuplicateSerial, persistDevices, onRefresh, setLocalErrorForIndex, clearLocalErrorForIndex],
  )

  // remove device (per-index loading)
  const removeDevice = useCallback(
    async (index) => {
      setRemoveLoading((p) => ({ ...p, [index]: true }))
      try {
        if (index < 0 || index >= devices.length) return
        const newDevices = devices.filter((_, i) => i !== index)
        const prev = devices
        setDevices(newDevices)
        try {
          await persistDevices(newDevices)
        } catch {
          setDevices(prev)
        }
        // clear local error for that index (and shift others is OK)
        setLocalDeviceErrors((prev) => {
          const next = {}
          Object.keys(prev).forEach((k) => {
            const ki = Number(k)
            if (ki < index) next[ki] = prev[k]
            else if (ki > index) next[ki - 1] = prev[k] // shift keys down by 1
          })
          return next
        })
        if (onRefresh) await onRefresh()
      } finally {
        setRemoveLoading((p) => ({ ...p, [index]: false }))
      }
    },
    [devices, persistDevices, onRefresh],
  )

  const startInlineEdit = useCallback((device, index) => {
    setInlineEditingIndex(index)
    setEditFormData({ serial: device.serial, device_type: device.device_type ?? "" })
  }, [])

  // save inline edit — only set loading for the currently saving index
  const saveInlineEdit = useCallback(
    async (index) => {
      setSaveLoadingIndex(index)
      try {
        const serial = editFormData.serial
        const device_type = editFormData.device_type
        if (hasDuplicateSerial(serial, index)) {
          // set inline error for this index
          setLocalErrorForIndex(index, { field: "serial", message: "Duplicate IMEI/Gateway ID detected. Please use a unique serial." })
          toast.error("Duplicate IMEI/Gateway ID detected. Please use a unique serial.")
          return
        } else {
          clearLocalErrorForIndex(index)
        }
        const updated = devices.map((d, i) => (i === index ? { ...d, serial, device_type } : d))
        const prev = devices
        setDevices(updated)
        try {
          await persistDevices(updated)
          setInlineEditingIndex(null)
          setEditFormData({ serial: "", device_type: "" })
          if (onRefresh) await onRefresh()
        } catch {
          setDevices(prev)
        }
      } finally {
        setSaveLoadingIndex(null)
      }
    },
    [editFormData, hasDuplicateSerial, devices, persistDevices, onRefresh, setLocalErrorForIndex, clearLocalErrorForIndex],
  )

  const cancelInlineEdit = useCallback(() => {
    setInlineEditingIndex(null)
    setEditFormData({ serial: "", device_type: "" })
  }, [])

  // helper: find a matching server error by serial (useful if server returns errors keyed by serial)
  const findServerErrorBySerial = useCallback(
    (serial) => {
      if (!Array.isArray(deviceErrors) || deviceErrors.length === 0) return null
      const norm = normalizeSerial(serial)
      return deviceErrors.find((e) => {
        // common server error shapes: { index, field, message } or { serial, field, message }
        if (e.serial && normalizeSerial(e.serial) === norm) return true
        if (typeof e.index === "number") return false // index-only errors handled elsewhere
        // fallback: check message includes serial (rare)
        if (e.message && typeof e.message === "string" && e.message.includes(serial)) return true
        return false
      })
    },
    [deviceErrors, normalizeSerial],
  )

  // combined getter: local errors first, then deviceErrors by index, then server errors by serial
  const getDeviceError = useCallback(
    (idx, device) => {
      if (localDeviceErrors && localDeviceErrors[idx]) return localDeviceErrors[idx]
      const byIndex = deviceErrors.find((e) => e.index === idx)
      if (byIndex) return byIndex
      if (device && device.serial) {
        const bySerial = findServerErrorBySerial(device.serial)
        if (bySerial) return bySerial
      }
      return null
    },
    [localDeviceErrors, deviceErrors, findServerErrorBySerial],
  )

  if (loadingBox) {
    return (
      <section className={styles.container}>
        <div style={{ padding: 20 }}>Loading box details...</div>
      </section>
    )
  }

  if (!box) {
    return (
      <section className={styles.container}>
        <div style={{ padding: 20 }}>No box selected or failed to load box details.</div>
      </section>
    )
  }

  return (
    <section className={styles.container}>
      <header className={styles.containerHeader}>
        <p>Box No. {box.box_number}</p>
        <FaRegEdit
          size={20}
          onClick={() => {
            if (devices.length === 0) {
              updateBox(box)
            }
          }}
          style={{ opacity: devices.length > 0 ? 0.5 : 1, cursor: devices.length > 0 ? "not-allowed" : "pointer" }}
          title={devices.length > 0 ? "Cannot edit box after adding devices" : "Edit box"}
        />
      </header>

      <main className={styles.devicesContainer}>
        <header className={styles.deviceHeader}>
          <section className={styles.tableHeader}>
            <h5>Box Type</h5>
            <p>{box.box_device_type_name ?? (box.device_type ? getDeviceTypeName(box.device_type) : "Tenovi")}</p>
          </section>

          <section className={styles.tableHeader}>
            <h5>Status</h5>
            <p>{getBoxTypeLabel(box.box_type)}</p>
          </section>

          <section className={styles.tableHeader}>
            <h5>Inventory Status</h5>
            <p>{box.inventory_status}</p>
          </section>
        </header>

        <main className={styles.devicesListing}>
          {devices.map((device, index) => {
            const error = getDeviceError(index, device)
            const isEditing = inlineEditingIndex === index
            const deviceTypeName = getDeviceTypeName(device.device_type)

            return (
              <section
                key={`${device.serial || "no-serial"}-${index}`}
                className={`${styles.device} ${error ? styles.deviceError : ""} ${isEditing ? styles.editingDevice : ""}`}
              >
                <h5>{index + 1}</h5>

                {isEditing ? (
                  <>
                    <section className={styles.deviceInput}>
                      <h5>Serial No</h5>
                      <div>
                        <SerialInput
                          value={editFormData.serial}
                          onChange={(val) => setEditFormData((p) => ({ ...p, serial: val }))}
                          deviceType={deviceTypeName}
                          label={deviceTypeName === "Tenovi Hub" ? "Gateway ID" : "IMEI Number"}
                        />
                        {error?.field === "serial" && <span className={styles.errorText}>{error.message}</span>}
                      </div>
                    </section>

                    <section className={styles.deviceInput}>
                      <h5>Device Type</h5>
                      <div>
                        <Select
                          options={typeOptions}
                          value={editFormData.device_type}
                          onChange={(val) => setEditFormData((p) => ({ ...p, device_type: val }))}
                          placeholder="Select device type"
                          width="100%"
                        />
                        {error?.field === "device_type" && <span className={styles.errorText}>{error.message}</span>}
                      </div>
                    </section>

                    <section className={styles.deviceActions}>
                      <FaCheck
                        className={`${styles.action} ${styles.saveAction} ${saveLoadingIndex === index ? styles.loading : ""}`}
                        onClick={() => saveLoadingIndex !== index && saveInlineEdit(index)}
                        style={{ opacity: saveLoadingIndex === index ? 0.6 : 1, cursor: saveLoadingIndex === index ? "not-allowed" : "pointer" }}
                      />
                      <FontAwesomeIcon className={`${styles.action} ${styles.cancelAction}`} icon={faTrash} onClick={cancelInlineEdit} />
                    </section>
                  </>
                ) : (
                  <>
                    <section className={styles.deviceInput}>
                      <h5>Serial No</h5>
                      <div>
                        <p>{device.serial}</p>
                        {error?.field === "serial" && <span className={styles.errorText}>{error.message}</span>}
                      </div>
                    </section>

                    <section className={styles.deviceInput}>
                      <h5>Device Type</h5>
                      <div>
                        <p>{deviceTypeName}</p>
                        {error?.field === "device_type" && <span className={styles.errorText}>{error.message}</span>}
                      </div>
                    </section>

                    <section className={styles.deviceActions}>
                      <FaRegEdit className={styles.action} onClick={() => startInlineEdit(device, index)} />
                      <FontAwesomeIcon
                        className={`${styles.action} ${removeLoading[index] ? styles.loading : ""}`}
                        icon={faTrash}
                        onClick={() => !removeLoading[index] && removeDevice(index)}
                        style={{ opacity: removeLoading[index] ? 0.6 : 1, cursor: removeLoading[index] ? "not-allowed" : "pointer" }}
                      />
                    </section>
                  </>
                )}
              </section>
            )
          })}

          {showAddDevice ? (
            <AddDevice
              setHandler={setDevice}
              boxDetails={box}
              editingDevice={editingDevice}
              onCancel={() => {
                setShowAddDevice(false)
                setEditingDevice(null)
              }}
            />
          ) : (
            <section className={styles.actionContainer}>
              <Button variant="primary" size="small" onClick={() => setShowAddDevice(true)}>
                Add Device
              </Button>
            </section>
          )}
        </main>
      </main>
    </section>
  )
}

export default React.memo(AddDevices)